import { ArrowDownOutlined, ArrowUpOutlined, SearchOutlined } from "@ant-design/icons";
import { Checkbox, Input } from "antd";
import { useMemo } from "react";
import styled from "styled-components";
import type { SortOrder, TableSortState } from "../types";

export type HeaderDropdownType = "filter" | "sort";

type SortLabels = {
	ascend: string;
	descend: string;
};

type TableHeaderDropdownProps = {
	type: HeaderDropdownType;
	columnKey: string;
	sortState: TableSortState | null;
	filterOptions: string[];
	selectedFilters: string[];
	filterSearch: string;
	onSortSelect: (columnKey: string, order: SortOrder | null) => void;
	onFilterSearchChange: (columnKey: string, value: string) => void;
	onFilterToggle: (columnKey: string, value: string) => void;
	sortLabels?: SortLabels;
};

const DropdownShell = styled.div`
	width: 280px;
	background: #ffffff;
	border-radius: 14px;
	box-shadow: 0 14px 28px rgba(39, 74, 143, 0.12);
	overflow: hidden;
`;

const DropdownSection = styled.div`
	padding: 10px 12px;
	border-bottom: 1px solid #eef0f4;
`;

const OptionList = styled.div`
	max-height: 240px;
	overflow-y: auto;
`;

const OptionRow = styled.button<{ $active?: boolean }>`
	width: 100%;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px 16px;
	border: none;
	background: ${(props) => (props.$active ? "#f7f8fc" : "#ffffff")};
	color: #35373d;
	font-size: 14px;
	line-height: 1;
	text-align: left;
	cursor: pointer;

	&:hover {
		background: #f7f8fc;
	}
`;

const OptionDivider = styled.div`
	height: 1px;
	background: #eef0f4;
`;

const CheckboxRow = styled.label`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 14px 16px;
	cursor: pointer;
	font-size: 14px;
	color: #4c4f55;

	&:hover {
		background: #f7f8fc;
	}
`;

const EmptyState = styled.div`
	padding: 18px 16px;
	font-size: 14px;
	color: #9094a0;
`;

export function TableHeaderDropdown(props: TableHeaderDropdownProps) {
	const {
		type,
		columnKey,
		sortState,
		filterOptions,
		selectedFilters,
		filterSearch,
		onSortSelect,
		onFilterSearchChange,
		onFilterToggle,
		sortLabels,
	} = props;

	const labels = sortLabels || {
		ascend: "Ascending",
		descend: "Descending",
	};

	const visibleOptions = useMemo(() => {
		const searchTerm = filterSearch.trim().toLowerCase();
		if (!searchTerm) {
			return filterOptions;
		}

		return filterOptions.filter((option) => option.toLowerCase().includes(searchTerm));
	}, [filterOptions, filterSearch]);

	const getNextSortOrder = (order: SortOrder): SortOrder | null => {
		if (sortState?.columnKey === columnKey && sortState.order === order) {
			return null;
		}

		return order;
	};

	if (type === "sort") {
		return (
			<DropdownShell>
				<OptionRow
					$active={sortState?.columnKey === columnKey && sortState.order === "ascend"}
					onClick={(e) => {
						e.stopPropagation();
						onSortSelect(columnKey, getNextSortOrder("ascend"));
					}}
				>
					<ArrowUpOutlined style={{ fontSize: 22 }} />
					<span>{labels.ascend}</span>
				</OptionRow>
				<OptionDivider />
				<OptionRow
					$active={sortState?.columnKey === columnKey && sortState.order === "descend"}
					onClick={(e) => {
						e.stopPropagation();
						onSortSelect(columnKey, getNextSortOrder("descend"));
					}}
				>
					<ArrowDownOutlined style={{ fontSize: 22 }} />
					<span>{labels.descend}</span>
				</OptionRow>
			</DropdownShell>
		);
	}

	return (
		<DropdownShell>
			<DropdownSection>
				<Input
					value={filterSearch}
					onChange={(e) => onFilterSearchChange(columnKey, e.target.value)}
					placeholder="Search to filter"
					prefix={<SearchOutlined style={{ color: "#aeb1ba" }} />}
				/>
			</DropdownSection>
			<OptionList>
				{visibleOptions.length === 0 && <EmptyState>No results</EmptyState>}
				{visibleOptions.map((option) => (
					<CheckboxRow key={option}>
						<Checkbox
							checked={selectedFilters.includes(option)}
							onChange={() => onFilterToggle(columnKey, option)}
						/>
						<span>{option}</span>
					</CheckboxRow>
				))}
			</OptionList>
		</DropdownShell>
	);
}
